export const MAX_FREQUENT_MENU_ITEMS = 5;
export const MAX_PINNED_MENU_ITEMS = 10;

export interface MenuNavEntry {
  path: string;
  label: string;
  parentLabel?: string;
  styleKey: string;
  permission?: { module: string; section: string; action: string } | null;
}

type UsageStore = Record<string, { count: number; lastUsed: number }>;

type RawMenuItem = {
  label: string;
  to?: string;
  permission?: { module: string; section: string; action: string } | null;
  submenu?: RawMenuItem[];
};

function storageKey(userId: string): string {
  return `menu-usage:${userId}`;
}

function pinsStorageKey(userId: string): string {
  return `menu-pins:${userId}`;
}

export function normalizeMenuPath(path: string): string {
  if (!path) return '';
  const [pathname] = path.split('?');
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

export function recordMenuVisit(userId: string, path: string): void {
  const normalized = normalizeMenuPath(path);
  if (!normalized || normalized === '/') return;

  try {
    const raw = localStorage.getItem(storageKey(userId));
    const store: UsageStore = raw ? JSON.parse(raw) : {};
    const existing = store[normalized] || { count: 0, lastUsed: 0 };
    store[normalized] = {
      count: existing.count + 1,
      lastUsed: Date.now(),
    };
    localStorage.setItem(storageKey(userId), JSON.stringify(store));
  } catch {
    // localStorage unavailable
  }
}

export function getMostUsedMenuPaths(userId: string): string[] {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const store: UsageStore = JSON.parse(raw);
    const merged: UsageStore = {};

    for (const [path, stats] of Object.entries(store)) {
      const normalized = normalizeMenuPath(path);
      if (!normalized) continue;
      const existing = merged[normalized] || { count: 0, lastUsed: 0 };
      merged[normalized] = {
        count: existing.count + stats.count,
        lastUsed: Math.max(existing.lastUsed, stats.lastUsed),
      };
    }

    return Object.entries(merged)
      .sort((a, b) => b[1].count - a[1].count || b[1].lastUsed - a[1].lastUsed)
      .map(([path]) => path);
  } catch {
    return [];
  }
}

export function flattenMenuNavigation(
  items: RawMenuItem[],
  options?: { parentLabel?: string; styleKey?: string }
): MenuNavEntry[] {
  const entries: MenuNavEntry[] = [];

  for (const item of items) {
    const styleKey = options?.styleKey || item.label;
    const parentLabel = options?.parentLabel;

    if (item.to) {
      entries.push({
        path: normalizeMenuPath(item.to),
        label: item.label,
        parentLabel,
        styleKey,
        permission: item.permission,
      });
    }

    if (item.submenu?.length) {
      entries.push(
        ...flattenMenuNavigation(item.submenu, {
          parentLabel: parentLabel || item.label,
          styleKey: item.label,
        })
      );
    }
  }

  return entries;
}

export function buildMenuRegistry(entries: MenuNavEntry[]): Map<string, MenuNavEntry> {
  const map = new Map<string, MenuNavEntry>();
  for (const entry of entries) {
    if (!entry.path) continue;
    const existing = map.get(entry.path);
    if (!existing || (entry.parentLabel && !existing.parentLabel)) {
      map.set(entry.path, entry);
    }
  }
  return map;
}

export function getPinnedMenuPaths(userId: string): string[] {
  try {
    const raw = localStorage.getItem(pinsStorageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const seen = new Set<string>();
    const paths: string[] = [];
    for (const path of parsed) {
      if (typeof path !== 'string') continue;
      const normalized = normalizeMenuPath(path);
      if (!normalized || seen.has(normalized)) continue;
      seen.add(normalized);
      paths.push(normalized);
    }
    return paths;
  } catch {
    return [];
  }
}

export function isMenuPathPinned(userId: string, path: string): boolean {
  const normalized = normalizeMenuPath(path);
  if (!normalized) return false;
  return getPinnedMenuPaths(userId).includes(normalized);
}

export function togglePinnedMenuPath(
  userId: string,
  path: string,
  limit = MAX_PINNED_MENU_ITEMS
): { pinned: boolean; paths: string[] } {
  const normalized = normalizeMenuPath(path);
  if (!normalized || normalized === '/') {
    return { pinned: false, paths: getPinnedMenuPaths(userId) };
  }

  const current = getPinnedMenuPaths(userId);
  const index = current.indexOf(normalized);
  let next: string[];
  let pinned: boolean;

  if (index >= 0) {
    next = current.filter((p) => p !== normalized);
    pinned = false;
  } else {
    next = [...current, normalized].slice(-limit);
    pinned = true;
  }

  try {
    localStorage.setItem(pinsStorageKey(userId), JSON.stringify(next));
  } catch {
    // localStorage unavailable
  }

  return { pinned, paths: next };
}

export function resolvePinnedMenuEntries(
  userId: string,
  registry: Map<string, MenuNavEntry>,
  canAccess: (entry: MenuNavEntry) => boolean
): MenuNavEntry[] {
  const paths = getPinnedMenuPaths(userId);
  const results: MenuNavEntry[] = [];
  const seenPaths = new Set<string>();

  for (const path of paths) {
    const normalized = normalizeMenuPath(path);
    const entry = registry.get(normalized);
    if (!entry || seenPaths.has(normalized) || !canAccess(entry)) {
      continue;
    }
    seenPaths.add(normalized);
    results.push(entry);
  }

  return results;
}

export function resolveMostUsedMenuEntries(
  userId: string,
  registry: Map<string, MenuNavEntry>,
  canAccess: (entry: MenuNavEntry) => boolean,
  limit = MAX_FREQUENT_MENU_ITEMS,
  excludePaths?: Set<string> | string[]
): MenuNavEntry[] {
  const paths = getMostUsedMenuPaths(userId);
  const results: MenuNavEntry[] = [];
  const seenPaths = new Set<string>();
  const seenLabels = new Set<string>();
  const excluded = excludePaths
    ? new Set(
        [...excludePaths]
          .map((path) => normalizeMenuPath(path))
          .filter(Boolean)
      )
    : null;

  for (const path of paths) {
    const normalized = normalizeMenuPath(path);
    const entry = registry.get(normalized);
    if (
      !entry ||
      seenPaths.has(normalized) ||
      seenLabels.has(entry.label) ||
      (excluded && excluded.has(normalized)) ||
      !canAccess(entry)
    ) {
      continue;
    }
    seenPaths.add(normalized);
    seenLabels.add(entry.label);
    results.push(entry);
    if (results.length >= limit) break;
  }

  return results;
}
